/**
 * The network transport. Every Electrical audit-capture HTTP call lives here
 * and nowhere else.
 *
 * Ported from Csa/offline/net.js's shape. Screens do not import this module
 * directly — they import electricalApi.js, which decides between here and
 * the local store depending on whether the audit has been provisioned for
 * offline use. Keeping the transport separate is what makes that swap
 * possible without touching a single screen.
 *
 * Backend note: the /api/electrical/* endpoints below (beyond the ones
 * electricalApi.js already used - organizations/audits/people/schedule)
 * don't exist yet. This file documents the expected contract — request
 * shapes mirror sql/01_elec_schema.sql's elec_* tables - so the backend can
 * be built to match it. See BACKEND_ENCRYPTION.md-style handoff note: this
 * is schema/contract-first scaffolding, same as the rest of this module.
 */

import axios from "../../../APIs/axios";
import { config } from "../../../config";
import { newLocalId } from "./outboxRules";

const BASE = `${config.PATH}/api/elec`;

export { newLocalId };

/* ------------------------------------------------------------------ */
/* errors                                                              */
/* ------------------------------------------------------------------ */

/**
 * Server errors reach the screens in one shape, so no screen has to know how
 * axios nests things. `status` is what screens branch on — 409 in
 * particular, which is never a failure to show as "something went wrong": it
 * means someone else's edit is already there.
 */
export class ElectricalError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "ElectricalError";
    this.status = status || 0;
    this.body = body || null;
    this.isConflict = status === 409;
    this.isOffline = status === 0;
  }
}

function wrap(err) {
  if (err instanceof ElectricalError) return err;
  const res = err && err.response;
  if (!res) {
    return new ElectricalError(
      "No connection to the server. Your work is still on this device.",
      0,
      null
    );
  }
  const body = res.data || {};
  return new ElectricalError(body.message || body.error || "Something went wrong", res.status, body);
}

/* ------------------------------------------------------------------ */
/* transport                                                           */
/* ------------------------------------------------------------------ */

async function read(path, params) {
  try {
    const r = await axios.get(BASE + path, params ? { params } : undefined);
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

/** Every write goes through here, so one place decides how errors are shaped. */
async function mutate(method, path, body) {
  try {
    const r = await axios({ method, url: BASE + path, data: body });
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

/* ------------------------------------------------------------------ */
/* organizations/sites (new-audit client/site picker) - always online  */
/* ------------------------------------------------------------------ */

export const listOrganizations = (q) => read("/organizations", q ? { q } : null);

/**
 * The legacy master checklist, read by the old ReportModal.js flow. The
 * new-audit form's area/category picker uses getAreaCategory() below instead
 * - this is kept only for whatever still calls it on the old path. Cached at
 * module scope - see the comment on the export for why.
 */
let allDataPromise = null;
export function getAllData() {
  if (!allDataPromise) {
    allDataPromise = axios
      .get(`${config.PATH}/api/all-data`)
      .then((r) => r.data)
      .catch((err) => {
        allDataPromise = null; // let the next call retry instead of caching a failure
        throw wrap(err);
      });
  }
  return allDataPromise;
}

/* ------------------------------------------------------------------ */
/* framework (versioned checklist: area > category > checkpoint) */
/* ------------------------------------------------------------------ */

/**
 * The real /api/electrical/framework response is a FLAT array of checkpoint
 * rows under `checkpoints` - {code, area_no, area, category, checkpoint,
 * criticality, observation, recommendations, legal_reference, sort_order}, one
 * row per checkpoint, 669 of them. Every screen that reads a framework
 * (ElectricalAssess's rail, ElectricalQuestionCard, ElectricalPhotos'
 * AssignDialog, ElectricalExtras' area picker, electricalRepo's
 * codesForLevel/listNotes) was written against a nested area > category >
 * checkpoint tree instead, so this is normalised into that shape once, here,
 * rather than teaching every consumer to re-group the flat list itself.
 *
 * Also renames the row's own `checkpoint` field (the checkpoint text) to
 * `check_points`, matching sql/01_elec_schema.sql's column name and what
 * every consumer already reads - `checkpoint.checkpoint` would work but reads
 * like a typo everywhere it appears.
 */
function normalizeFramework(fw) {
  if (!fw) return fw;
  const flat = Array.isArray(fw) ? fw : fw.checkpoints || [];

  const byArea = new Map();
  flat.forEach((cp) => {
    if (!byArea.has(cp.area)) {
      byArea.set(cp.area, { area_no: cp.area_no, area: cp.area, categories: new Map() });
    }
    const categories = byArea.get(cp.area).categories;
    if (!categories.has(cp.category)) {
      categories.set(cp.category, { category: cp.category, checkpoints: [] });
    }
    categories.get(cp.category).checkpoints.push({
      code: cp.code,
      check_points: cp.checkpoint,
      criticality: cp.criticality,
      observation: cp.observation,
      recommendations: cp.recommendations,
      legal_reference: cp.legal_reference,
      sort_order: cp.sort_order,
      comparable_to_prior: cp.comparable_to_prior,
    });
  });

  const areas = [...byArea.values()]
    .sort((a, b) => (a.area_no ?? 0) - (b.area_no ?? 0))
    .map((a) => ({
      ...a,
      categories: [...a.categories.values()].sort(
        (x, y) => (x.checkpoints[0]?.sort_order ?? 0) - (y.checkpoints[0]?.sort_order ?? 0)
      ),
    }));

  return {
    version: fw.version,
    framework_name: fw.framework_name,
    checkpoint_count: fw.checkpoint_count,
    areas,
  };
}

/**
 * The framework is the same checkpoint set for every audit on a version, so
 * it's fetched once per session rather than per screen. This cache is also
 * the natural place for the offline layer to hand back the stored copy
 * instead (see electricalRepo.getFramework).
 */
let frameworkCache = null;

export async function getFramework(version) {
  if (!version && frameworkCache) return frameworkCache;
  const fw = await read("/framework", version ? { version } : null);
  const normalized = normalizeFramework(fw);
  if (!version) frameworkCache = normalized;
  return normalized;
}

export function clearFrameworkCache() {
  frameworkCache = null;
}

/**
 * The area/category picker on the new-audit form - flat, two levels
 * (area > category), unlike the full versioned framework above which also
 * carries every checkpoint. Resolves to:
 *   {areas: [{area_no, area, category: [{name}]}]}
 */
export const getAreaCategory = () => read("/area-category");

/* ------------------------------------------------------------------ */
/* the audit                                                           */
/* ------------------------------------------------------------------ */

export const createAudit = (fields) =>
  mutate("post", "/audit", { local_id: newLocalId(), ...fields });

/** My audits. `qs` is passed through as built by the caller - status, q, limit, offset. */
export const listAudits = (qs) => read("/audits" + (qs ? "?" + qs : ""));

export const getAudit = (id) => read(`/audit/${id}`);
export const getRollup = (id) => read(`/audit/${id}/rollup`);
export const getReadiness = (id) => read(`/audit/${id}/readiness`);

/**
 * Autosave one answer: criticality + status (+ observation/recommendations/
 * legal_reference text) for one checkpoint.
 *
 * `row_version` is what the caller last read. Omitting it disables the
 * check and lets a stale tab overwrite a colleague's edit, so callers must
 * pass it. A mismatch throws an ElectricalError with `isConflict` set and
 * the current value in `err.body.current`.
 */
export const saveAnswer = (id, answer) => mutate("post", `/audit/${id}/answer`, answer);

/**
 * Fill the blanks in one area or category with a compliant status (default
 * "YES" - the sector's own status_options decide the exact label).
 * Electrical's equivalent of CSA's bulk-yes: CSA has a single yes/no/partial/
 * na answer, Electrical scores a (criticality, status) pair, so "bulk
 * compliant" sets status only and leaves each checkpoint's own default
 * criticality alone.
 *
 * `area` disambiguates a category whose name recurs under more than one area
 * (e.g. "TR-Signage") - required whenever `level` is "category".
 */
export const bulkMarkCompliant = (id, level, level_value, status, area) =>
  mutate("post", `/audit/${id}/bulk-yes`, { level, level_value, area, status: status || "YES" });

/* ------------------------------------------------------------------ */
/* scope                                                               */
/* ------------------------------------------------------------------ */

export const addExclusion = (id, exclusion) =>
  mutate("post", `/audit/${id}/exclusion`, exclusion);
export const removeExclusion = (id, exclusionId) =>
  mutate("delete", `/audit/${id}/exclusion/${exclusionId}`);

/* ------------------------------------------------------------------ */
/* the note pad                                                        */
/* ------------------------------------------------------------------ */

/** `forCode` orders the list for the write-up screen; omit it for the pad. */
export const listNotes = (id, forCode) =>
  read(`/audit/${id}/notes`, forCode ? { for: forCode } : null);

export const addNote = (id, body, category) =>
  mutate("post", `/audit/${id}/notes`, { local_id: newLocalId(), body, category });

/** Copies the note into the checkpoint's observation — it does not link. */
export const useNote = (id, noteId, checkpoint_code) =>
  mutate("post", `/audit/${id}/notes/${noteId}/use`, { checkpoint_code });

export const deleteNote = (id, noteId) => mutate("delete", `/audit/${id}/notes/${noteId}`);

/* ------------------------------------------------------------------ */
/* off-checklist observations                                          */
/* ------------------------------------------------------------------ */

export const listExtras = (id) => read(`/audit/${id}/extras`);
export const addExtra = (id, extra) =>
  mutate("post", `/audit/${id}/extras`, { local_id: newLocalId(), ...extra });
export const updateExtra = (id, extraId, patch) =>
  mutate("put", `/audit/${id}/extras/${extraId}`, patch);
export const deleteExtra = (id, extraId) =>
  mutate("delete", `/audit/${id}/extras/${extraId}`);

/* ------------------------------------------------------------------ */
/* the write-up pass                                                   */
/* ------------------------------------------------------------------ */

/** Gaps only - checkpoints without a compliant status - with notes beside each. */
export const getGaps = (id) => read(`/audit/${id}/gaps`);

/* ------------------------------------------------------------------ */
/* carry-forward                                                       */
/* ------------------------------------------------------------------ */

/*
 * Straight to the network, like the client lookups above: a repeat visit is
 * set up at a desk, not on a plant floor. No local path, same as CSA's.
 */
export const carryPreview = (id) => read(`/audit/${id}/carry-preview`);
export const carryForward = (id) => mutate("post", `/audit/${id}/carry`, {});

/** Confirm named checkpoints, or every outstanding one with { all: true }. */
export const confirmCarried = (id, body) => mutate("post", `/audit/${id}/confirm`, body);

/** Last visit's answers, keyed by checkpoint code — the "last time: …" line. */
export const previousAnswers = (id) => read(`/audit/${id}/previous`);

/* ------------------------------------------------------------------ */
/* workflow                                                            */
/* ------------------------------------------------------------------ */

export const submitAudit = (id, opts) => mutate("post", `/audit/${id}/submit`, opts || {});
export const reviewAudit = (id, opts) => mutate("post", `/audit/${id}/review`, opts || {});
export const returnAudit = (id, reason) => mutate("post", `/audit/${id}/return`, { reason });

/** The auditor takes their own submitted audit back. No reason, unlike returnAudit. */
export const reopenAudit = (id) => mutate("post", `/audit/${id}/reopen`, {});

/* ------------------------------------------------------------------ */
/* offline                                                             */
/* ------------------------------------------------------------------ */

/** Replay the outbox. Returns one acknowledgement per operation sent. */
export const syncAudit = (id, device_id, operations) =>
  mutate("post", `/audit/${id}/sync`, { device_id, operations });

/** Record that this device holds the audit. Warns on take-over; never refuses. */
export const claimAudit = (id, device_id) =>
  mutate("post", `/audit/${id}/claim`, { device_id });

/* ------------------------------------------------------------------ */
/* photos                                                              */
/* ------------------------------------------------------------------ */

export const listPhotos = (id) => read(`/audit/${id}/photos`);

/**
 * Upload one photo.
 *
 * Deliberately not through `mutate`: this carries bytes, so it needs
 * multipart rather than JSON. The browser sets its own multipart boundary,
 * so Content-Type is left alone.
 *
 * Keyed server-side on (audit_id, local_id), so retrying after a dropped
 * connection overwrites the same object instead of leaving an orphan.
 */
export async function uploadPhoto(id, { local_id, blob, thumb, checkpoint_code, captured_at }) {
  const form = new FormData();
  form.append("local_id", local_id);
  if (captured_at) form.append("captured_at", captured_at);
  if (checkpoint_code) form.append("checkpoint_code", checkpoint_code);
  form.append("photo", blob, local_id + ".jpg");
  if (thumb) form.append("thumb", thumb, local_id + "_t.jpg");

  try {
    const r = await axios.post(BASE + `/audit/${id}/photos`, form,{
      headers: {
        "Content-Type": "multipart/form-data"
      }});
    return r.data;
  } catch (err) {
    throw wrap(err);
  }
}

export const linkPhoto = (id, local_id, checkpoint_code) =>
  mutate("post", `/audit/${id}/photos/${local_id}/links`, { checkpoint_code });

export const unlinkPhoto = (id, local_id, checkpoint_code) =>
  mutate("delete", `/audit/${id}/photos/${local_id}/links/${encodeURIComponent(checkpoint_code)}`);

export const deletePhoto = (id, local_id) =>
  mutate("delete", `/audit/${id}/photos/${local_id}`);

/**
 * A photograph's bytes, served by the API rather than fetched from S3
 * directly - the route needs the Authorization header a plain <img src>
 * cannot send, and going through the app's own origin sidesteps whatever a
 * site's locked-down network/proxy does to an unfamiliar S3 domain.
 */
export async function photoBytes(auditId, localId, size) {
  const r = await axios.get(
    `${BASE}/audit/${auditId}/photos/${encodeURIComponent(localId)}/bytes` +
      (size === "full" ? "?size=full" : ""),
    { responseType: "arraybuffer" }
  );
  return new Uint8Array(r.data);
}

/* ------------------------------------------------------------------ */
/* the report                                                          */
/* ------------------------------------------------------------------ */

/** Composed server-side. Only available once the audit is submitted. */
export const getReportModel = (id, opts = {}) =>
  read(`/audit/${id}/report`, {
    ...(opts.layout ? { layout: opts.layout } : {}),
    ...(opts.all ? { all: 1 } : {}),
    ...(opts.conformantRecs ? { conformant_recs: 1 } : {}),
  });

export const getReportContents = (id) => read(`/audit/${id}/report/contents`);

/** Either { preset } or { ids, is_selected }. */
export const setReportContents = (id, body) =>
  mutate("post", `/audit/${id}/report/contents`, body);

export const saveReportSection = (id, key, body) =>
  mutate("put", `/audit/${id}/report/section/${key}`, { body });

/**
 * Generate one narrative section with AI, from this audit's own findings —
 * the elec_prompt rows seeded for 'executive_summary', 'critical_observations',
 * 'way_forward' and 'conclusion' (sql/11_elec_prompt_seed.sql). A starting
 * draft, not a final answer: the server saves it as the section's current
 * body (same as a manual save would), and the auditor edits from there.
 *
 * Distinct from startDraft/draftItems below, which draft one sentence per
 * No/N/A checkpoint finding — this drafts a whole section's prose in one
 * synchronous call, so there is no job to poll.
 */
export const draftReportSection = (id, key) =>
  mutate("post", `/audit/${id}/report/section/${key}/draft`, {});

/**
 * Open an issue and get back the model to draw.
 *
 * The issue number is allocated server-side and stamped into the model's
 * header, because the document's own footer carries it — so the number has
 * to exist before anything renders, not after.
 */
export const openReportIssue = (id, body) =>
  mutate("post", `/audit/${id}/report/issue`, body);

export const listReportIssues = (id) => read(`/audit/${id}/report/issues`);
export const getReportIssue = (id, no) => read(`/audit/${id}/report/issues/${no}`);

/* ---------- drafting ---------- */

/**
 * Start a drafting run. 202 and a job id — the work happens in the
 * background, so this returns long before there is anything to read.
 */
export const startDraft = (id) => mutate("post", `/audit/${id}/draft`, {});

/** Where the run has got to. Polled while it is running. */
export const draftProgress = (id) => read(`/audit/${id}/draft`);

/** Stop it. What was already drafted is kept. */
export const cancelDraft = (id) => mutate("delete", `/audit/${id}/draft`);

/** The drafts, disagreements first — the server orders them that way. */
export const draftItems = (id) => read(`/audit/${id}/draft/items`);

/**
 * Put a draft into the report.
 *
 * `{ checkpoint_code }` for one, `{ all: true }` for every draft the two
 * passes agreed on. Bulk never takes a flagged one. `overwrite` is required
 * to replace a sentence an auditor wrote by hand.
 */
export const acceptDraft = (id, body) => mutate("post", `/audit/${id}/draft/accept`, body);

/* ------------------------------------------------------------------ */
/* discarding a draft audit                                            */
/* ------------------------------------------------------------------ */

export const previewDiscard = (id) => read(`/audit/${id}/discard-preview`);
export const discardAudit = (id) => mutate("delete", `/audit/${id}`);

/* ------------------------------------------------------------------ */
/* people (auditor/reviewer pickers)                                   */
/* ------------------------------------------------------------------ */

export const listPeople = (role) =>
  read("/people" + (role ? "?role=" + encodeURIComponent(role) : ""));

/* ------------------------------------------------------------------ */
/* scheduling                                                          */
/* ------------------------------------------------------------------ */

export const scheduleConflicts = (userId, start, end, excludeAuditId) =>
  read(
    "/schedule/conflicts?user_id=" + encodeURIComponent(userId) +
    "&start_date=" + encodeURIComponent(start) +
    "&end_date=" + encodeURIComponent(end) +
    (excludeAuditId ? "&exclude_audit_id=" + encodeURIComponent(excludeAuditId) : "")
  );
